import PubSub from "./PubSub";
import Event from "./Event";
import axios from "axios";
import Constants from "./Constants";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { App } from "@capacitor/app";

import { PushNotifications } from "@capacitor/push-notifications";

export default class API {
  static ENV_STAGING: any = "https://apiv2-staging.projectleannation.com";
  static ENV_PRODUCTION: any = "https://apiv2.projectleannation.com";
  //static ENV_PRODUCTION: any = "https://6ebe2ec9a065.ngrok-free.app";

  static ENV_DEV: any = "http://localhost:8080";
  //static ENV_DEV = "https://b33a-8-9-83-164.ngrok.io";

  static ENV_USER_TOKEN: any = "";
  static ENV_CURRENT: any = "";

  /**
   * Sets the environment
   *
   * @param {*} env
   */
  static setEnvironment(env: any) {
    this.ENV_CURRENT = env;

    PubSub.publish(Event.ENV_CHANGED);
  }

  /**
   * Gets the current auth token.
   */
  static getAuthToken() {
    return this.ENV_USER_TOKEN;
  }

  /**
   * Sets the user's auth token.
   *
   * @param {*} val
   */
  static setAuthToken(val: any) {
    this.ENV_USER_TOKEN = val;

    PubSub.publish(Event.USER_LOADED);
  }

  /**
   * Sends a generic request.
   *
   * @param {*} options
   */
  static _sendRequest(options: any) {
    if (this.ENV_CURRENT?.includes("ngrok")) {
      if (!options?.headers) {
        options.headers = {};
      }

      options.headers["ngrok-skip-browser-warning"] = "true";
    }

    if (this.ENV_CURRENT) {
      return axios(options);
    } else {
      return new Promise((resolve, reject) => {
        PubSub.subscribe(Event.ENV_CHANGED, () => {
          options.uri = this.ENV_CURRENT + options.uri;

          axios(options).then(
            (data) => {
              if (data) {
                resolve(data);
              }
            },
            (error) => {
              reject(error);
            },
          );
        });
      });
    }
  }

  /**
   * Sends an unauthenticed HTTP request.
   *
   * @param method
   * @param path
   * @param data
   */
  static _unauthenticatedRequest(
    method: any,
    path: any,
    data: any = null,
    externalRequest: boolean = false,
  ) {
    let options: any = {
      method: method,
      url: externalRequest ? path : this.ENV_CURRENT + path,
      json: true,
    };

    if (data) {
      options.data = data;
    }

    return this._sendRequest(options);
  }

  static scheduleAppointment(storeID: any = null, payload: any = null) {
    return this._unauthenticatedRequest(
      "POST",
      "/scheduler/stores/" + storeID,
      payload,
    );
  }

  static getExistingAppointments(
    storeID: any = null,
    type = "nc-consult",
    date: any = null,
  ) {
    return this._unauthenticatedRequest(
      "GET",
      `/scheduler/appointments/availability?storeID=${storeID}&type=${type}&date=${date}`,
    );
  }

  static getAppointmentByID(appointmentID: any = null) {
    return this._unauthenticatedRequest(
      "GET",
      "/scheduler/appointments/" + appointmentID,
    );
  }

  static cancelAppointment(appointmentID: any = null) {
    return this._unauthenticatedRequest(
      "DELETE",
      "/scheduler/appointments/" + appointmentID,
    );
  }

  static rescheduleAppointment(
    appointmentID: any = null,
    dateTime: any = null,
  ) {
    return this._unauthenticatedRequest(
      "PUT",
      "/scheduler/appointments/" + appointmentID + "/reschedule",
      {
        dateTime: dateTime,
      },
    );
  }

  static uploadPrivateFile(file, randomFileName = true) {
    return axios({
      method: "POST",
      url:
        this.ENV_CURRENT +
        "/global/cdn/private/open?token=4Vg5ZbqlKANhJ1vhPX5oRGXa2P2vo31T",
      data: file,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total ?? 1),
        );
        console.log(`Upload progress: ${percent}%`);
      },
    });
  }

  /**
   * Gets a testimonial video template by id.
   *
   * @param id
   * @returns
   */
  static getTestimonialTemplate(id) {
    return this._unauthenticatedRequest(
      "GET",
      "/public/testimonial-templates/" + id,
    );
  }

  /**
   * Submits a testimonial video.
   *
   * @param testimonialID
   * @param payload
   * @returns
   */
  static submitTestimonialRecording(
    testimonialID: any = null,
    payload: any = null,
  ) {
    return this._unauthenticatedRequest(
      "POST",
      `/public/testimonials/${testimonialID}/recording`,
      payload,
    );
  }

  /**
   * Submits a testimonial video.
   *
   * @param testimonialID
   * @param payload
   * @returns
   */
  static submitTestimonial(testimonialID: any = null, payload: any = null) {
    return this._unauthenticatedRequest(
      "POST",
      `/public/testimonials/${testimonialID}/contact`,
      payload,
    );
  }

  /**
   * Creates or updates a lead by email.
   *
   * @param {*} email
   * @param {*} zip
   * @param {*} source
   * @param {*} prms
   * @returns
   */
  static createOrUpdateLead(
    email: any = null,
    zip: any = null,
    source: any = null,
    prms: any = {},
  ) {
    let params = prms ? prms : {};

    if (email) {
      params.email = email;
    }

    if (zip) {
      params.zipCode = zip.toString();
    }

    if (source) {
      params.source = source;
    }

    return this._unauthenticatedRequest(
      "POST",
      "/customer-account/lead?v=2",
      params,
    );
  }

  static updateLeadCart(
    lead: any = null,
    cartState: any = {},
    forceOverwrite = false,
  ) {
    let payload: any = {
      storeID: lead.storeID ? lead.storeID : "",
      email: lead.email ? lead.email : "",
      phone: lead.phone ? lead.phone : "",
      zipCode: lead.zipCode ? lead.zipCode.toString() : "",
      utmSource: lead.utmSource ? lead.utmSource : "",
      utmMedium: lead.utmMedium ? lead.utmMedium : "",
      utmCampaign: lead.utmCampaign ? lead.utmCampaign : "",
      utmContent: lead.utmContent ? lead.utmContent : "",
      utmTerms: lead.utmTerms ? lead.utmTerms : "",
      promo: lead.promo ? lead.promo : "",
      cartState,
      forceOverwrite,
    };

    return this._unauthenticatedRequest(
      "POST",
      "/customer-account/lead?v=2",
      payload,
    );
  }

  /**
   * Sends a basic authenticated HTTP request.
   *
   * @param method
   * @param path
   * @param username
   * @param password
   * @param data
   */
  static _basicAuthRequest(
    method: any = null,
    path: any = null,
    username: any = null,
    password: any = null,
    data: any = null,
  ) {
    let options: any = {
      method: method,
      url: this.ENV_CURRENT + path,
      json: true,
      auth: {
        username: username,
        password: password,
      },
    };

    if (data) {
      options.data = data;
    }

    return this._sendRequest(options);
  }

  /**
   * Sends a bearer authenticated HTTP request.
   *
   * @param method
   * @param path
   * @param token
   * @param data
   */
  static _bearerAuthRequest(
    method: any = null,
    path: any = null,
    data: any = null,
  ) {
    let options: any = {
      method: method,
      url: this.ENV_CURRENT + path,
      json: true,
      headers: { Authorization: `Bearer ${this.getAuthToken()}` },
    };

    if (data) {
      options.data = data;
    }

    return this._sendRequest(options);
  }

  static getStores() {
    return this._unauthenticatedRequest("GET", "/public/stores");
  }

  static getEmailPreferences(payload: any = null) {
    return this._unauthenticatedRequest(
      "POST",
      "/public/communications/preferences",
      payload,
    );
  }

  static manageEmailPreferences(payload: any = null) {
    return this._unauthenticatedRequest(
      "POST",
      "/public/communications/unsubscribe",
      payload,
    );
  }

  static getStoresForZip(zip: any = null, withGeolocation = false) {
    const url =
      "/public/stores?zip=" +
      zip +
      "&withGeolocation=" +
      (withGeolocation ? "true" : "false");

    return this._unauthenticatedRequest("GET", url);
  }

  static getStoreByID(id: any = null) {
    return this._unauthenticatedRequest("GET", "/public/stores/" + id);
  }

  static getMealByID(id: any = null) {
    return this._unauthenticatedRequest("GET", "/public/meals/" + id);
  }

  static getMealReviews(mealIDs: any = null) {
    return this._unauthenticatedRequest(
      "GET",
      "/public/meals/reviews?mealIDs=" + JSON.stringify(mealIDs),
    );
  }

  static getCouponByCode(id: any = null, storeID: any = null) {
    return this._unauthenticatedRequest(
      "GET",
      "/public/discounts/" + id + (storeID ? "?storeID=" + storeID : ""),
    );
  }

  static getCartState(id: any = null) {
    return this._unauthenticatedRequest("GET", "/customer-account/lead/" + id);
  }

  /*
  * Deprecated legacy endpoints
  static authenticateCustomer(email: any = "") {
    return this._unauthenticatedRequest(
      "GET",
      "/customer-account/authenticate?email=" + email
    );
  }

  static authenticateCustomerWithPassword(email: any = "", password: any = "") {
    return this._basicAuthRequest(
      "GET",
      "/customer-account/authenticate/password",
      email,
      password
    );
  }*/

  static setPassword(token: any = null, password: any = null, code = null) {
    let payload: any = {
      token,
      password,
      code,
    };

    return this._unauthenticatedRequest(
      "PUT",
      "/customer-account/authenticate/password/reset",
      payload,
    );
  }

  /**
   * Checks if an app user account exists.
   *
   * @param email
   * @returns
   */
  static appUserAccountExists(email: any = null) {
    return this._unauthenticatedRequest(
      "GET",
      "/app-users/exists?email=" + email,
    );
  }

  /**
   * Creates a new app user.
   *
   * @param email
   * @param password
   * @returns
   */
  static createAppUser(email: any = null, password: any = null) {
    let payload: any = {
      email,
      password,
    };

    return this._unauthenticatedRequest("POST", "/app-users", payload);
  }

  /**
   * Authenticates an app user.
   *
   * @param email
   * @param password
   * @returns
   */
  static authenticateAppUser(email: any = "", password: any = "") {
    return this._basicAuthRequest(
      "GET",
      "/app-users/authenticate",
      email,
      password,
    );
  }

  /**
   * Sends a verification code to the user
   *
   * @param email
   * @returns
   */
  static sendVerificationCode(email: any = null) {
    return this._unauthenticatedRequest(
      "GET",
      "/app-users/authenticate/verification?email=" + email,
    );
  }

  /**
   * Gets a password reset token
   *
   * @param verificationToken
   * @param code
   * @returns
   */
  static getPasswordResetToken(verificationToken: any, code: any) {
    return this._unauthenticatedRequest(
      "GET",
      "/app-users/authenticate/password/reset-token?token=" +
        verificationToken +
        "&code=" +
        code,
    );
  }

  /**
   * Resets the password for an app user
   *
   * @param resetToken
   * @param password
   * @returns
   */
  static resetAppUserPassword(resetToken: any, password: any) {
    return this._unauthenticatedRequest(
      "PUT",
      "/app-users/authenticate/password/reset?token=" + resetToken,
      {
        password,
      },
    );
  }

  /**
   * Get app user account
   *
   * @returns
   */
  /**
   * Get transfer availability for the current member
   *
   * @param storeID
   * @param planID
   * @returns
   */
  static getTransferAvailability(storeID?: string, planID?: string) {
    let url = "/app-users/me/transfer/availability";

    if (storeID && planID) {
      url += `?storeID=${storeID}&planID=${planID}`;
    }

    return this._bearerAuthRequest("GET", url);
  }

  /**
   * Execute a membership store transfer
   *
   * @param storeID
   * @param planID
   * @returns
   */
  static executeTransfer(storeID: string, planID: string) {
    return this._bearerAuthRequest("POST", "/app-users/me/transfer", {
      storeID,
      planID,
    });
  }

  static async getAccount() {
    let promise : any = null;

    if (Capacitor.isNativePlatform()) {
      let device = await Device.getInfo();
      let app = await App.getInfo();
      let deviceID = await Device.getId();

      let notifications = await PushNotifications.checkPermissions();

      let appInfo: any = {
        appVersion: app.version,
        appBuild: app.build,
        deviceModel: device.model,
        devicePlatform: device.platform,
        deviceOperatingSystem: device.operatingSystem,
        deviceOSVersion: device.osVersion,
        deviceAndroidSDKVersion: device.androidSDKVersion,
        deviceManufacturer: device.manufacturer,
        deviceWebviewVersion: device.webViewVersion,
        deviceID: deviceID.identifier,
        pushNotifications: notifications?.receive == "granted",
      };

      let url = `/app-users/me?appInfo=${JSON.stringify(appInfo)}`;

      promise = this._bearerAuthRequest("GET", url);
    } else {
      promise = this._bearerAuthRequest("GET", "/app-users/me");
    }

    let win : any = window;

    if (win.betterstack) {
      promise.then((response: any) => {
        if (response.data && win?.betterstack) {

          let userPayload : any = {
            id: response.data.data?.user?._id,
            name: response.data.data?.user?.name,
            email: response.data.data?.user?.email,
            store_id: response.data.data?.store?._id ? response.data.data?.store?._id : null,
            store_name: response.data.data?.store?.name ? response.data.data?.store?.name : null,
            //plan: response.data.data?.user?.plan,
          };

          console.log(response.data);

          console.log(userPayload);

          win.betterstack('user', userPayload);
        }
      });
    }

    return promise;
  }

  /**
   * Deletes the user's online account
   *
   * @returns
   */
  static deleteOnlineAccount() {
    return this._bearerAuthRequest(
      "DELETE",
      "/app-users/me/delete-online-account",
      {},
    );
  }

  /**
   * Updates the user's contact information
   *
   * @param name
   * @param email
   * @param phone
   * @param instagramHandle
  * @returns
   */
  static updateProfile(name: any = null, email: any = null, phone: any = null, instagramHandle: any = null) {
    let payload: any = {
      name,
      email,
      phone,
      instagramHandle, 
    };

    //   console.log(payload)

    return this._bearerAuthRequest("PUT", "/app-users/me/profile", payload);
  }

  /**
   * Changes the user's password
   *
   * @param currentPassword
   * @param password
   * @returns
   */
  static changePassword(currentPassword: any = null, password: any = null) {
    let payload: any = {
      current: currentPassword,
      password,
    };

    return this._bearerAuthRequest("PUT", "/app-users/me/password", payload);
  }

  static setPasswordNewAccount(email: any = null, password: any = null) {
    let payload: any = {
      email,
      password,
    };

    return this._unauthenticatedRequest(
      "POST",
      "/customer-account/authenticate/password",
      payload,
    );
  }

  static async getCustomerAccount(email: any = "") {
    if (Capacitor.isNativePlatform()) {
      let device = await Device.getInfo();
      let app = await App.getInfo();
      let deviceID = await Device.getId();

      let notifications = await PushNotifications.checkPermissions();

      let appInfo: any = {
        appVersion: app.version,
        appBuild: app.build,
        deviceModel: device.model,
        devicePlatform: device.platform,
        deviceOperatingSystem: device.operatingSystem,
        deviceOSVersion: device.osVersion,
        deviceAndroidSDKVersion: device.androidSDKVersion,
        deviceManufacturer: device.manufacturer,
        deviceWebviewVersion: device.webViewVersion,
        deviceID: deviceID.identifier,
        pushNotifications: notifications?.receive == "granted",
      };

      let url = `/customer-account/account?appInfo=${JSON.stringify(appInfo)}`;

      return this._bearerAuthRequest("GET", url);
    } else {
      return this._bearerAuthRequest("GET", "/customer-account/account");
    }
  }

  // TODO: reimplement
  static skipCustomerOrder(email = "", interval = null, afterCutoff = false) {
    let payload: any = {
      email: email,
      after_cutoff: afterCutoff,
    };

    return this._unauthenticatedRequest(
      "PUT",
      "/customer-account/skip",
      payload,
    );
  }

  static unskipCustomerOrder() {
    return this._bearerAuthRequest("PUT", "/customer-account/unskip");
  }

  /**
   * Cancels a customer's subscription.
   *
   * @param customerID
   * @param reason
   * @param notes
   * @returns
   */
  static cancelCustomerSubscription(
    customerID: any = null,
    reason: any = "",
    notes: any = "",
  ) {
    let payload: any = {
      customerID,
      reason,
      notes,
    };

    return this._unauthenticatedRequest(
      "PUT",
      "/customer-accounts/cancel",
      payload,
    );
  }

  // TODO: reimplement
  static activateCustomerSubscription() {
    return this._bearerAuthRequest("PUT", "/customer-account/activate/v2", {});
  }

  // TODO: reimplement
  static updateMealsForCustomer(email = "", meals = []) {
    let payload: any = {
      email: email,
      meals: meals,
    };

    return this._unauthenticatedRequest(
      "POST",
      "/customer-account/meals",
      payload,
    );
  }

  static fetchUserNotifications(page: number, limit: number = 10) {
    return this._bearerAuthRequest(
      "GET",
      `/app-users/me/notifications?page=${page ? page : 1}&limit=${
        limit > 0 ? limit : 10
      }&orderby=${JSON.stringify({ timestamp: -1 })}`,
    );
  }

  static setNotificationsAsRead(ids: Array<string> = []) {
    return this._bearerAuthRequest("PUT", `/app-users/me/notifications/read`, {
      notificationIDs: ids,
    });
  }

  static getAppointments(
    page: number,
    limit: number = 10,
    filters = {},
    orderBy = {},
  ) {
    return this._bearerAuthRequest(
      "GET",
      `/app-users/me/appointments?filters=${JSON.stringify(filters)}&page=${
        page ? page : 1
      }&count=${limit > 0 ? limit : 10}&orderby=${JSON.stringify(orderBy)}`,
    );
  }

  static fetchUserOrders(filters = {}, page: number, limit: number = 10) {
    return this._bearerAuthRequest(
      "GET",
      `/customer-account/orders?filters=${JSON.stringify(filters)}&page=${
        page ? page : 1
      }&count=${limit > 0 ? limit : 10}&orderby=${JSON.stringify({
        fulfillmentDate: -1,
      })}`,
    );
  }

  static resendOrderReceipt(id = "") {
    return this._bearerAuthRequest(
      "PUT",
      `/customer-account/orders/${id}/resend-receipt`,
    );
  }

  /**
   * Captures meal feedback
   *
   * @param mealID
   * @param rating
   * @param heatingMethods
   * @param comments
   * @returns
   */
  static submitMealFeedback(
    mealID: string,
    rating = "",
    heatingMethods: any = [],
    comments = "",
    storeID = "",
  ) {
    let payload: any = {
      rating,
      heatingMethods,
      comments,
      mealID,
      storeID,
    };

    return this._bearerAuthRequest(
      "POST",
      "/customer-account/meals/feedback",
      payload,
    );
  }

  static getMyMealFeedback(mealIDs: string[]) {
    return this._bearerAuthRequest(
      "GET",
      `/customer-account/meals/my-feedback?mealIDs=${encodeURIComponent(
        JSON.stringify(mealIDs),
      )}`,
    );
  }

  // TODO: reimplement
  static checkout(
    leadID: any = null,
    plan: any = null,
    meals: any = null,
    authData: any = null,
    billingData: any = null,
    shippingData: any = null,
    coupon: any = null,
    referredBy: any = null,
    marketingOptIn = false,
    firstBillDate = "",
    chooseLater = false,
  ) {
    let params: any = {
      leadID,
      plan: plan,
      meals: meals,

      billing: billingData,
      shipping: shippingData ? shippingData : null,
      coupon: coupon ? coupon : null,
      referredBy: referredBy,
      marketingOptIn: marketingOptIn ? true : false,
      chooseLater: chooseLater ? true : false,
    };

    if (!authData?.provider || authData?.provider != "stripe") {
      params.paymentAuth = authData;
      params.paymentProvider = "auth.net";
    } else {
      params.paymentProvider = "stripe";
      params.stripeCustomerID = authData.stripeCustomerID;
      params.stripePaymentMethod = authData.stripePaymentMethod;
    }

    if (firstBillDate) {
      params.firstBillDate = firstBillDate;
    }

    return this._unauthenticatedRequest(
      "POST",
      "/customer-account/checkout?v=2",
      params,
    );
  }

  // TODO: reimplement
  static reactivate(
    plan: any = null,
    meals: any = null,
    authData: any = null,
    billingData: any = null,
    shippingData: any = null,
    coupon: any = null,
  ) {
    let params = {
      plan: plan,
      meals: meals,
      payment_auth: authData,
      billing: billingData,
      shipping: shippingData ? shippingData : null,
      coupon: coupon ? coupon : null,
    };

    return this._unauthenticatedRequest(
      "POST",
      "/customer-accounts/reactivate",
      params,
    );
  }

  /**
   * Updates the payment method for a user.
   *
   * @param {*} data
   * @returns
   */
  static updatePaymentMethod(data: any = null) {
    let payload: any = {
      paymentAuth: data,
    };

    return this._bearerAuthRequest(
      "PUT",
      "/customer-account/account/payment",
      payload,
    );
  }

  /**
   * Updates the payment method for a user.
   *
   * @param {*} data
   * @returns
   */
  static changeCustomerPlan(planID: any = null) {
    let payload: any = {
      planID,
    };

    return this._bearerAuthRequest(
      "PUT",
      "/customer-account/account/plan",
      payload,
    );
  }

  /**
   * Sets a key value paramneter for a user
   * @param key
   * @param value
   * @returns
   */
  static setUserParameter(key: string, value: any = null) {
    let payload: any = {
      key,
      value,
    };

    return this._bearerAuthRequest(
      "PUT",
      "/customer-account/account/parameters",
      payload,
    );
  }

  /**
   * Sets a key value paramneter for a user
   * @param key
   * @param value
   * @returns
   */
  static setAppUserParameter(key: string, value: any = null) {
    let payload: any = {
      key,
      value,
    };

    return this._bearerAuthRequest("PUT", "/app-users/me/parameters", payload);
  }

  /**
   * Sets a key value paramneter for a user
   * @param key
   * @param value
   * @returns
   */
  static setupRetailLoyaltyAccount(name: string, email: string, phone: string) {
    let payload: any = {
      name,
      email,
      phone,
    };

    return this._bearerAuthRequest(
      "PUT",
      "/app-users/me/retail-loyalty",
      payload,
    );
  }

  /**
   * Updates a user's billing address
   *
   * @param {*} name
   * @param {*} address_1
   * @param {*} address_2
   * @param {*} city
   * @param {*} state
   * @param {*} zip
   * @param {*} phone
   * @returns
   */
  static updateBillingAddress(
    name: any = null,
    address_1: any = null,
    address_2: any = null,
    city: any = null,
    state: any = null,
    zip: any = null,
    phone: any = null,
  ) {
    let payload: any = {
      name,
      address_1,
      address_2,
      city,
      state,
      zip,
      phone,
    };

    return this._bearerAuthRequest(
      "PUT",
      "/customer-account/account/address/billing",
      payload,
    );
  }

  /**
   * Updates a user's shipping address
   *
   * @param {*} name
   * @param {*} address_1
   * @param {*} address_2
   * @param {*} city
   * @param {*} state
   * @param {*} zip
   * @param {*} phone
   * @returns
   */
  static updateShippingAddress(
    name: any = null,
    address_1: any = null,
    address_2: any = null,
    city: any = null,
    state: any = null,
    zip: any = null,
    phone: any = null,
  ) {
    let payload: any = {
      name,
      address_1: address_1,
      address_2,
      city,
      state,
      zip,
      phone,
    };

    return this._bearerAuthRequest(
      "PUT",
      "/customer-account/account/address/shipping",
      payload,
    );
  }

  static logSkipFeedback(nextBillDate = "", reason = "") {
    return this._bearerAuthRequest("POST", "/customer-account/pause/feedback", {
      nextBillDate,
      reason,
    });
  }

  static signInWithGoogleLeads(data: any = null) {
    return this._bearerAuthRequest(
      "POST",
      "/public/webhooks/google/oauth/leads/redirect",
      data,
    );
  }

  static signInWithGoogle(data: any = null) {
    return this._bearerAuthRequest(
      "POST",
      "/public/webhooks/google/oauth/redirect",
      data,
    );
  }

  static signInWithApple(data: any = null, nonce = "", clientId = "") {
    return this._bearerAuthRequest(
      "POST",
      `/public/webhooks/apple/oauth/redirect?nonce=${nonce}&clientID=${clientId}`,
      data,
    );
  }

  static getStripeCheckoutIntent(email: any) {
    return this._unauthenticatedRequest(
      "POST",
      "/customer-account/checkout/stripe/payment-intent",
      {
        email,
      },
    );
  }

  /**
   * Reverse geocodes a lat long into an address
   *
   * @param lat
   * @param lng
   * @returns
   */
  static reverseGeocodeLatLng(lat: any = null, lng: any = null) {
    return this._bearerAuthRequest(
      "GET",
      `/public/reverse-geocode?lat=${lat}&lng=${lng}&withStores=true`,
      null,
    );
  }

  static applePayMerchantVerification(url: any = "") {
    return this._unauthenticatedRequest(
      "POST",
      `/public/webhooks/apple/merchant-verification`,
      {
        validationURL: url,
      },
      false,
    );
  }

  static applePayTest(opaqueData: any = null) {
    return this._unauthenticatedRequest(
      "POST",
      `/public/webhooks/apple/checkout-test`,
      {
        opaqueData,
      },
      false,
    );
  }

  /**
   * Reverse geocodes a lat long into an address
   *
   * @param lat
   * @param lng
   * @returns
   */
  static getPLNPlusPasskitPass(customerID: String) {
    return axios({
      method: "GET",
      url: `${this.ENV_CURRENT}/public/passkit/${customerID}`,
      responseType: "blob",
    });
  }

  /**
   * Marks an order as picked up
   *
   * @param orderID
   * @returns
   */
  static markOrderAsPickedUp(orderID: String) {
    return this._bearerAuthRequest(
      "PUT",
      `/customer-account/orders/${orderID}/status`,
      {
        status: "CLAIMED",
      },
    );
  }
}
